import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Currency, initialCurrency } from '../models/currency'
import {
  createCurrency,
  deleteCurrency,
  getCurrencies,
  updateCurrency,
  getCurrency,
} from '../api/currenciesApi'
import { KTSVG } from '../../../../../_dashboard/helpers/components/KTSVG'
import { toast } from 'react-toastify'
import { Tooltip, OverlayTrigger } from 'react-bootstrap'

const CurrencyTab: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [currencyToDelete, setCurrencyToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const pageSize = 10

  const loadCurrencies = async () => {
    try {
      setLoading(true)
      const response = await getCurrencies(page, pageSize)
      setCurrencies(response.data || [])
      setTotalPages(response.payload.pagination.totalPages || 0)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CURRENCY.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCurrencies()
  }, [page])

  const currencySchema = Yup.object().shape({
    code: Yup.string().required('Code is required').uppercase(),
    name: Yup.string().required('Name is required'),
    symbol: Yup.string().required('Symbol is required'),
    rateToBase: Yup.number()
      .required('Rate to base is required')
      .min(0, 'Must be greater than or equal to 0'),
    symbolPosition: Yup.string().oneOf(['before', 'after']).required('Symbol position is required'),
    decimalSeparator: Yup.string().required('Decimal separator is required').max(1, 'Must be a single character'),
    thousandsSeparator: Yup.string().required('Thousands separator is required').max(1, 'Must be a single character'),
    decimalPlaces: Yup.number()
      .required('Decimal places is required')
      .min(0, 'Must be greater than or equal to 0')
      .max(4, 'Must be less than or equal to 4'),
    isActive: Yup.boolean().required('Status is required'),
  })

  const formik = useFormik({
    initialValues: editingCurrency || initialCurrency,
    validationSchema: currencySchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (editingCurrency?._id) {
          await updateCurrency(editingCurrency._id, values)
          toast.success(intl.formatMessage({ id: 'TOUR_CURRENCY.SUCCESS.UPDATE' }))
        } else {
          await createCurrency(values)
          toast.success(intl.formatMessage({ id: 'TOUR_CURRENCY.SUCCESS.CREATE' }))
        }
        resetForm()
        setEditingCurrency(null)
        setShowModal(false)
        loadCurrencies()
      } catch (error) {
        toast.error(
          intl.formatMessage({
            id: editingCurrency?._id
              ? 'TOUR_CURRENCY.ERROR.UPDATE'
              : 'TOUR_CURRENCY.ERROR.CREATE',
          })
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleDelete = async () => {
    if (!currencyToDelete) return

    try {
      setIsDeleting(true)
      await deleteCurrency(currencyToDelete)
      toast.success(intl.formatMessage({ id: 'TOUR_CURRENCY.SUCCESS.DELETE' }))
      loadCurrencies()
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CURRENCY.ERROR.DELETE' }))
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
      setCurrencyToDelete(null)
    }
  }

  const handleEdit = async (currency: Currency) => {
    try {
      setLoading(true)
      const response = await getCurrency(currency._id!)
      setEditingCurrency(response.data)
      formik.setValues(response.data)
      setShowModal(true)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CURRENCY.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditingCurrency(null)
    formik.resetForm()
    setShowModal(false)
  }

  const handleAddNew = () => {
    setEditingCurrency(null)
    formik.resetForm()
    setShowModal(true)
  }

  return (
    <div className='card card-custom'>
      {/* Header with Add Button */}
      <div className='card-header border-0 pt-6'>
        <div className='card-title'></div>
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end'>
            <button type='button' className='btn btn-primary' onClick={handleAddNew}>
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
              {intl.formatMessage({ id: 'TOUR_CURRENCY.ADD_NEW' })}
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className='card-body pt-0'>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center min-h-300px'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed fs-6 gy-5'>
                <thead>
                  <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                    <th>{intl.formatMessage({ id: 'TOUR_CURRENCY.CODE' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CURRENCY.NAME' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CURRENCY.SYMBOL' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CURRENCY.RATE' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CURRENCY.FORMAT' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CURRENCY.STATUS' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CURRENCY.ACTIONS' })}</th>
                  </tr>
                </thead>
                <tbody className='text-gray-600 fw-semibold'>
                  {currencies.map((currency) => (
                    <tr key={currency._id}>
                      <td>{currency.code}</td>
                      <td>{currency.name}</td>
                      <td>{currency.symbol}</td>
                      <td>{currency.rateToBase}</td>
                      <td>
                        {`${currency.symbolPosition === 'before' ? currency.symbol : ''} 1${
                          currency.thousandsSeparator
                        }234${currency.decimalSeparator}${currency.decimalPlaces === 0 ? '00' : '56'}${
                          currency.symbolPosition === 'after' ? currency.symbol : ''
                        }`}
                      </td>
                      <td>
                        <span
                          className={`badge badge-light-${
                            currency.isActive ? 'success' : 'danger'
                          } fw-bold`}
                        >
                          {intl.formatMessage({ id: currency.isActive ? 'STATUS.ACTIVE' : 'STATUS.INACTIVE' })}
                        </span>
                      </td>
                      <td>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({ id: 'ACTION.EDIT' })}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-primary btn-sm me-1'
                            onClick={() => handleEdit(currency)}
                          >
                            <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({ id: 'ACTION.DELETE' })}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-danger btn-sm'
                            onClick={() => {
                              if (currency._id) {
                                setCurrencyToDelete(currency._id)
                                setShowDeleteConfirm(true)
                              }
                            }}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className='d-flex justify-content-between align-items-center flex-wrap'>
                <div className='d-flex flex-wrap py-2 mr-3'>
                  <button
                    className='btn btn-light btn-active-light-primary me-2'
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.PREVIOUS' })}
                  </button>
                  <button
                    className='btn btn-light btn-active-light-primary'
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.NEXT' })}
                  </button>
                </div>
                <div className='d-flex align-items-center py-3'>
                  <span className='text-muted'>
                    {intl.formatMessage(
                      { id: 'PAGINATION.INFO' },
                      {
                        page: page + 1,
                        total: totalPages,
                      }
                    )}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1050}}
        >
          <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-650px'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>
                  {editingCurrency
                    ? intl.formatMessage({ id: 'TOUR_CURRENCY.EDIT' })
                    : intl.formatMessage({ id: 'TOUR_CURRENCY.ADD_NEW' })}
                </h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={handleCancel}
                  disabled={formik.isSubmitting}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <form onSubmit={formik.handleSubmit}>
                <div className='modal-body px-8 py-6'>
                  <div className='row mb-6'>
                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.CODE' })}
                        </label>
                        <input
                          type='text'
                          className={`form-control ${
                            formik.touched.code && formik.errors.code ? 'is-invalid' : ''
                          }`}
                          placeholder={intl.formatMessage({ id: 'TOUR_CURRENCY.CODE_PLACEHOLDER' })}
                          {...formik.getFieldProps('code')}
                        />
                        {formik.touched.code && formik.errors.code && (
                          <div className='invalid-feedback'>{formik.errors.code}</div>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.NAME' })}
                        </label>
                        <input
                          type='text'
                          className={`form-control ${
                            formik.touched.name && formik.errors.name ? 'is-invalid' : ''
                          }`}
                          placeholder={intl.formatMessage({ id: 'TOUR_CURRENCY.NAME_PLACEHOLDER' })}
                          {...formik.getFieldProps('name')}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <div className='invalid-feedback'>{formik.errors.name}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.SYMBOL' })}
                        </label>
                        <input
                          type='text'
                          className={`form-control ${
                            formik.touched.symbol && formik.errors.symbol ? 'is-invalid' : ''
                          }`}
                          placeholder={intl.formatMessage({ id: 'TOUR_CURRENCY.SYMBOL_PLACEHOLDER' })}
                          {...formik.getFieldProps('symbol')}
                        />
                        {formik.touched.symbol && formik.errors.symbol && (
                          <div className='invalid-feedback'>{formik.errors.symbol}</div>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.RATE' })}
                        </label>
                        <input
                          type='number'
                          step='0.0001'
                          className={`form-control ${
                            formik.touched.rateToBase && formik.errors.rateToBase ? 'is-invalid' : ''
                          }`}
                          {...formik.getFieldProps('rateToBase')}
                        />
                        {formik.touched.rateToBase && formik.errors.rateToBase && (
                          <div className='invalid-feedback'>{formik.errors.rateToBase}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.SYMBOL_POSITION' })}
                        </label>
                        <select
                          className={`form-select ${
                            formik.touched.symbolPosition && formik.errors.symbolPosition ? 'is-invalid' : ''
                          }`}
                          {...formik.getFieldProps('symbolPosition')}
                        >
                          <option value='before'>
                            {intl.formatMessage({ id: 'TOUR_CURRENCY.SYMBOL_POSITION_BEFORE' })}
                          </option>
                          <option value='after'>
                            {intl.formatMessage({ id: 'TOUR_CURRENCY.SYMBOL_POSITION_AFTER' })}
                          </option>
                        </select>
                        {formik.touched.symbolPosition && formik.errors.symbolPosition && (
                          <div className='invalid-feedback'>{formik.errors.symbolPosition}</div>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.DECIMAL_PLACES' })}
                        </label>
                        <input
                          type='number'
                          min='0'
                          max='4'
                          className={`form-control ${
                            formik.touched.decimalPlaces && formik.errors.decimalPlaces ? 'is-invalid' : ''
                          }`}
                          {...formik.getFieldProps('decimalPlaces')}
                        />
                        {formik.touched.decimalPlaces && formik.errors.decimalPlaces && (
                          <div className='invalid-feedback'>{formik.errors.decimalPlaces}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.DECIMAL_SEPARATOR' })}
                        </label>
                        <input
                          type='text'
                          maxLength={1}
                          className={`form-control ${
                            formik.touched.decimalSeparator && formik.errors.decimalSeparator ? 'is-invalid' : ''
                          }`}
                          {...formik.getFieldProps('decimalSeparator')}
                        />
                        {formik.touched.decimalSeparator && formik.errors.decimalSeparator && (
                          <div className='invalid-feedback'>{formik.errors.decimalSeparator}</div>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.THOUSANDS_SEPARATOR' })}
                        </label>
                        <input
                          type='text'
                          maxLength={1}
                          className={`form-control ${
                            formik.touched.thousandsSeparator && formik.errors.thousandsSeparator ? 'is-invalid' : ''
                          }`}
                          {...formik.getFieldProps('thousandsSeparator')}
                        />
                        {formik.touched.thousandsSeparator && formik.errors.thousandsSeparator && (
                          <div className='invalid-feedback'>{formik.errors.thousandsSeparator}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_CURRENCY.STATUS' })}
                        </label>
                        <div className='form-check form-switch form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            checked={formik.values.isActive}
                            onChange={(e) => {
                              formik.setFieldValue('isActive', e.target.checked)
                            }}
                            id='statusSwitch'
                          />
                          <label className='form-check-label' htmlFor='statusSwitch'>
                            {intl.formatMessage({
                              id: formik.values.isActive ? 'STATUS.ACTIVE' : 'STATUS.INACTIVE',
                            })}
                          </label>
                        </div>
                        {formik.touched.isActive && formik.errors.isActive && (
                          <div className='invalid-feedback d-block'>{formik.errors.isActive}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                  <button
                    type='button'
                    className='btn btn-light-primary fw-semibold px-6'
                    onClick={handleCancel}
                    disabled={formik.isSubmitting}
                  >
                    {intl.formatMessage({ id: 'ACTION.CANCEL' })}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary fw-semibold px-8'
                    disabled={formik.isSubmitting || !formik.isValid}
                  >
                    {formik.isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                        <span className='ms-2'>{intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}</span>
                      </>
                    ) : (
                      intl.formatMessage({ id: editingCurrency ? 'ACTION.UPDATE' : 'ACTION.SAVE' })
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1050}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>
                  {intl.formatMessage({ id: 'HEADER.DELETE' })}
                </h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setCurrencyToDelete(null)
                  }}
                  disabled={isDeleting}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <div className='modal-body px-8 py-6'>
                {intl.formatMessage({ id: 'TOUR_CURRENCY.DELETE_CONFIRM' })}
              </div>
              <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                <button
                  type='button'
                  className='btn btn-light-primary fw-semibold px-6'
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setCurrencyToDelete(null)
                  }}
                  disabled={isDeleting}
                >
                  {intl.formatMessage({ id: 'ACTION.CANCEL' })}
                </button>
                <button
                  type='button'
                  className='btn btn-danger fw-semibold px-6'
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                      <span className='ms-2'>{intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}</span>
                    </>
                  ) : (
                    intl.formatMessage({ id: 'ACTION.DELETE' })
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CurrencyTab 